/**
 * Transforma os dados do formulário do formato do frontend (camelCase, objetos aninhados)
 * para o formato esperado pelo backend (snake_case, arrays planos).
 */

import {
  CustomerType,
  OnboardingFormData,
  PersonaFisicaForm,
  PessoaJuridicaForm,
  CreditQuestionnaireForm,
  LandTransaction,
  CropType,
  CottonProductionBySeason,
  CropProductionBySeason,
} from '../types';
import { UploadedFileInfo } from '../services/api';

// ============ Helpers ============

function toSnakeCase(str: string): string {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}

function keysToSnakeCase(obj: unknown): unknown {
  if (Array.isArray(obj)) return obj.map(keysToSnakeCase);
  if (obj !== null && typeof obj === 'object' && !(obj instanceof Date)) {
    return Object.fromEntries(
      Object.entries(obj as Record<string, unknown>).map(([key, value]) => [
        toSnakeCase(key),
        keysToSnakeCase(value),
      ])
    );
  }
  return obj;
}

// ============ PERSONAL DATA ============

function transformPFPersonalData(pf: PersonaFisicaForm) {
  const id = pf.identification;
  return {
    identification: {
      full_name: id.fullName,
      cpf: id.cpf,
      marital_status: id.maritalStatus,
      nationality: id.nationality,
      birth_date: id.birthDate,
      document_type: id.documentType,
      document_number: id.documentNumber,
      document_uf: id.documentUf,
      gender: id.gender,
      mother_name: id.motherName,
      spouse_name: id.spouseName || null,
      spouse_cpf: id.spouseCpf || null,
      main_activity: pf.professional?.mainActivity || '',
    },
  };
}

function transformPJPersonalData(pj: PessoaJuridicaForm) {
  const id = pj.identification;
  return {
    identification: {
      company_name: id.companyName,
      cnpj: id.cnpj,
      main_activity: id.mainActivity,
      cnae_code: id.cnaeCode,
      constitution_date: id.constitutionDate,
      contact_person: pj.contactPerson,
      contact_phone: pj.contactPhone,
    },
    financial: {
      capital: pj.financial?.capital,
      country: pj.financial?.country,
      annual_revenue: pj.financial?.annualRevenue,
      net_worth: pj.financial?.netWorth,
    },
    shareholders: (pj.shareholders || []).map((s) => ({
      name: s.name,
      cpf_cnpj: s.cpfCnpj,
      share_percentage: s.sharePercentage,
    })),
    final_beneficiaries: (pj.finalBeneficiaries || []).map((b) => ({
      name: b.name,
      cpf: b.cpf,
      share_percentage: b.sharePercentage,
    })),
  };
}

// ============ ADDRESS DATA ============

function transformAddresses(
  customerType: CustomerType,
  pf?: PersonaFisicaForm,
  pj?: PessoaJuridicaForm
) {
  // 1. Mudamos de Array para Objeto
  const addresses: Record<string, unknown> = {};

  if (customerType === 'PF' && pf) {
    if (pf.residentialAddress) {
      // 2. Usamos a chave do tipo de endereço em vez de fazer .push()
      addresses['residential'] = keysToSnakeCase(pf.residentialAddress);
    }
    if (pf.commercialAddress) {
      addresses['commercial'] = {
        ...(keysToSnakeCase(pf.commercialAddress) as Record<string, unknown>),
        same_as_residential: pf.sameAsResidential || false,
      };
    }
  } else if (customerType === 'PJ' && pj) {
    if (pj.headquartersAddress) {
      addresses['headquarters'] = keysToSnakeCase(pj.headquartersAddress);
    }
    if (pj.correspondenceAddress) {
      addresses['correspondence'] = keysToSnakeCase(pj.correspondenceAddress);
    }
  }

  return addresses;
}

// ============ CREDIT QUESTIONNAIRE ============

function transformCreditQuestionnaire(cq: CreditQuestionnaireForm) {
  const cd = cq.clientData;
  const cp = cd?.contactPreference;

  return {
    client_data: {
      producer_name: cd?.producerName || '',
      cpf: cd?.cpf || '',
      phone: cd?.phone || '',
      plants_in_group: cd?.plantsInGroup || false,
      financed_season: cd?.financedSeason || '',
      can_contact: cp?.canContact || false,
      contact_name: cp?.contactName || null,
      contact_position: cp?.contactPosition || null,
      contact_email: cp?.contactEmail || null,
      contact_phone: cp?.contactPhone || null,
    },
    group_members: (cd?.groupMembers || []).map((m) => ({
      name: m.name,
      cpf: m.cpf,
      relationship: m.relationship,
      marital_status: m.maritalStatus,
      spouse_name: m.spouseName || null,
      spouse_cpf: m.spouseCpf || null,
    })),
    patrimony: transformPatrimony(cq.patrimony),
    production: transformProduction(cq.productionArea, cq.production),
    commercialization: transformCommercialization(cq.commercialization),
    investments: transformInvestments(cq.investments),
    debt: transformDebt(cq.debt),
  };
}

// ---- Patrimônio ----

function transformPatrimony(patrimony: CreditQuestionnaireForm['patrimony']) {
  if (!patrimony) {
    return { land_properties: [], land_transactions: [], other_assets: null };
  }

  const landProperties = (patrimony.landProperties || []).map((p) => ({
    property_name: p.propertyName,
    location: p.location,
    owner: p.owner,
    planting_area: p.plantingArea,
    cattle_area: p.cattleArea,
    reserve_area: p.reserveArea,
    value_usd: p.valueUsd,
  }));

  // Mesclar landPurchases e landSales em um único array land_transactions
  const landTransactions: unknown[] = [];

  const mapTransaction = (t: LandTransaction, type: string) => ({
    transaction_type: type,
    farm_name: t.farmName,
    total_area: t.totalArea,
    transaction_date: t.transactionDate,
    transfer: t.transfer,
    total_value: t.totalValue,
    payments: Object.entries(t.payments || {}).map(([safra, valueSacas]) => ({
      safra,
      value_sacas: valueSacas,
    })),
  });

  (patrimony.landPurchases || []).forEach((t) =>
    landTransactions.push(mapTransaction(t, 'purchase'))
  );
  (patrimony.landSales || []).forEach((t) =>
    landTransactions.push(mapTransaction(t, 'sale'))
  );

  // other_assets: flatten de objetos aninhados para campos planos
  const oa = patrimony.otherAssets;
  const otherAssets = oa
    ? {
        warehouses_value: oa.warehouses?.value ?? null,
        warehouses_capacity: oa.warehouses?.capacity ?? null,
        cotton_gin_value: oa.cottonGin?.value ?? null,
        cotton_gin_capacity: oa.cottonGin?.capacity ?? null,
        machinery_value: oa.machinery?.value ?? null,
        infrastructure_value: oa.infrastructure?.value ?? null,
        others_value: oa.others?.value ?? null,
        others_description: oa.others?.description ?? null,
      }
    : null;

  return {
    land_properties: landProperties,
    land_transactions: landTransactions,
    other_assets: otherAssets,
  };
}

// ---- Produção ----

const COTTON_CROPS: CropType[] = ['algodao', 'algodao_safrinha'];

function isCottonProduction(
  data: CropProductionBySeason | CottonProductionBySeason
): data is CottonProductionBySeason {
  return 'plumePrice' in data;
}

function transformProduction(
  productionArea: CreditQuestionnaireForm['productionArea'],
  production: CreditQuestionnaireForm['production']
) {
  // owned_areas: Record<Safra, Area> → [{safra, ...}]
  const ownedAreas = Object.entries(productionArea?.ownedArea || {}).map(
    ([safra, area]) => ({
      safra,
      planting_area: area.plantingArea,
      cattle_area: area.cattleArea,
      reserve_area: area.reserveArea,
    })
  );

  // leased_areas: merge leasedArea (received) e leasedToThirdParties (ceded)
  const leasedAreas: unknown[] = [];

  const mapLeased = (
    entries: Record<string, { leasedArea: number; avgCostSoy: number; avgCostCorn: number; avgContractDuration: number }>,
    leaseType: string
  ) => {
    Object.entries(entries || {}).forEach(([safra, area]) => {
      leasedAreas.push({
        lease_type: leaseType,
        safra,
        leased_area: area.leasedArea,
        avg_cost_soy: area.avgCostSoy || null,
        avg_cost_corn: area.avgCostCorn || null,
        avg_contract_duration: area.avgContractDuration || null,
      });
    });
  };

  mapLeased(productionArea?.leasedArea as Record<string, never>, 'received');
  mapLeased(productionArea?.leasedToThirdParties as Record<string, never>, 'ceded');

  // crops e cotton: Record<CropType, Record<Safra, Production>> → arrays planos
  const crops: unknown[] = [];
  const cotton: unknown[] = [];

  Object.entries(production?.cropProduction || {}).forEach(([cropType, safraMap]) => {
    Object.entries(safraMap || {}).forEach(([safra, data]) => {
      if (COTTON_CROPS.includes(cropType as CropType) && isCottonProduction(data)) {
        cotton.push({
          crop: cropType,
          safra,
          planted_area: data.plantedArea,
          plume_price: data.plumePrice,
          seed_price: data.seedPrice,
          plume_productivity: data.plumeProductivity,
          seed_productivity: data.seedProductivity,
          cost: data.cost,
          contracts: data.contracts,
        });
      } else {
        const d = data as CropProductionBySeason;
        crops.push({
          crop: cropType,
          safra,
          planted_area: d.plantedArea,
          productivity: d.productivity,
          avg_price: d.avgPrice,
          cost: d.cost,
          contracts: d.contracts,
        });
      }
    });
  });

  // cattle: Record<Safra, CattleProduction> → [{safra, ...}]
  const cattle = Object.entries(production?.cattleProduction || {}).map(
    ([safra, data]) => ({
      safra,
      avg_price: data.avgPrice,
      heads: data.heads,
      cost: data.cost,
      herd: data.herd,
    })
  );

  // stock: já é array, só renomear chaves
  const stock = (production?.stock || []).map((s) => ({
    crop: s.crop,
    volume: s.volume,
    warehouse: s.warehouse,
  }));

  return {
    owned_areas: ownedAreas,
    leased_areas: leasedAreas,
    crops,
    cotton,
    cattle,
    stock,
  };
}

// ---- Comercialização ----

function transformCommercialization(
  commercialization: CreditQuestionnaireForm['commercialization']
) {
  // Record<Safra, FixedContract[]> → array plano com safra em cada item
  const fixedContracts: unknown[] = [];

  Object.entries(commercialization?.fixedContracts || {}).forEach(
    ([safra, contracts]) => {
      (contracts || []).forEach((c) => {
        fixedContracts.push({
          safra,
          trading: c.trading,
          commodity: c.commodity,
          volume_tons: c.volumeTons,
          price_usd: c.priceUsd,
        });
      });
    }
  );

  return { fixed_contracts: fixedContracts };
}

// ---- Investimentos ----

function transformInvestments(
  investmentData: CreditQuestionnaireForm['investments']
) {
  // Record<Safra, {items, sources}> → { items: [{safra, ..., sources: [...]}] }
  const items: unknown[] = [];

  Object.entries(investmentData?.investments || {}).forEach(([safra, data]) => {
    const inv = data.items || ({} as Record<string, number>);
    const sources = Object.entries(data.sources || {}).map(([key, src]) => ({
      item_key: toSnakeCase(key),
      own_value: src.own ?? null,
      third_party_value: src.thirdParty ?? null,
    }));

    items.push({
      safra,
      land_opening: inv.landOpening ?? null,
      storage: inv.storage ?? null,
      soil_correction: inv.soilCorrection ?? null,
      irrigation: inv.irrigation ?? null,
      fleet_renewal: inv.fleetRenewal ?? null,
      others: inv.others ?? null,
      sources,
    });
  });

  return { items };
}

// ---- Endividamento ----

function transformDebt(debt: CreditQuestionnaireForm['debt']) {
  if (!debt) {
    return {
      suppliers: [],
      tradings: [],
      banks: [],
      credit_limits: [],
      third_parties: [],
    };
  }

  return {
    suppliers: (debt.supplierDebts || []).map((d) => ({
      company: d.company,
      value_usd: d.valueUsd,
      payment_2024: d.payment2024,
      remaining_balance: d.remainingBalance,
    })),
    tradings: (debt.tradingDebts || []).map((d) => ({
      trading: d.trading,
      value_usd: d.valueUsd,
      payment_2024: d.payment2024,
      remaining_balance: d.remainingBalance,
    })),
    banks: (debt.bankDebts || []).map((d) => ({
      bank: d.bank,
      modality: d.modality,
      currency: d.currency,
      start_date: d.startDate,
      due_date: d.dueDate,
      initial_value: d.initialValue,
      current_balance: d.currentBalance,
      payment_2024: d.payment2024,
      guarantees: d.guarantees,
    })),
    credit_limits: (debt.creditLimits || []).map((d) => ({
      bank: d.bank,
      approved_limit: d.approvedLimit,
      currency: d.currency,
      updated_value: d.updatedValue,
      usage_2024: d.usage2024,
      usage_2025: d.usage2025,
      usage_2026: d.usage2026,
    })),
    third_parties: (debt.thirdPartyDebts || []).map((d) => ({
      person_or_company: d.personOrCompany,
      relationship: d.relationship,
      currency: d.currency,
      value: d.value,
      due_date: d.dueDate,
      guarantee: d.guarantee,
    })),
  };
}

// ============ UPLOADED FILES ============

function transformUploadedFiles(files: Record<string, UploadedFileInfo>) {
  //Adicionei todos os campos que precisamos para construir o path, mover e renomear o arquivo
  return Object.values(files).map((f) => ({
    field_name: f.field_name,
    file_name: f.file_name,
    file_size: f.file_size,
    mime_type: f.mime_type,
    storage_key: f.storage_key || '',
    
    // Adicionando tags mockadas temporariamente para validar o path builder.
    //Teste validando um arquivo como Transação de proposta
    tags: {
      "grupo_id": "grupo-teste",
      "empresa_id": "empresa-teste",
      "transacao_id": "transacao_teste",
      "proposta_id": "proposta_teste"
    }
  }));
}

// ============ EXPORT PRINCIPAL ============

export function transformSubmitData(
  token: string,
  formData: Partial<OnboardingFormData>,
  customerType: CustomerType,
  uploadedFiles: Record<string, UploadedFileInfo>
) {
  const pf = formData.pessoaFisica;
  const pj = formData.pessoaJuridica;
  const cq = formData.creditQuestionnaire;

  return {
    token,
    personal_data:
      customerType === 'PF' && pf
        ? transformPFPersonalData(pf)
        : customerType === 'PJ' && pj
          ? transformPJPersonalData(pj)
          : {},
    address_data: transformAddresses(customerType, pf, pj),
    credit_questionnaire: cq ? transformCreditQuestionnaire(cq) : {},
    authorizations: keysToSnakeCase(formData.authorizations),
    uploaded_files: transformUploadedFiles(uploadedFiles),
  };
}
